/**
 * PulseArticle — /pulse-article/:slug
 *
 * Public rendering of an AI-generated Content Engine article.
 * Renders: hero image + attribution, title, meta, Listen button,
 * language selector (40+ langs), key facts, sections, FAQs, sources,
 * freshness stamp.
 *
 * Data flow:
 *   1. GET /api/content-engine/articles/:slug            (master payload)
 *   2. On language change: GET .../:slug/i18n?lang=xx    (translated on demand + cached)
 *   3. ListenButton reads plain text extracted from the currently
 *      displayed payload.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  BadgeCheck, Calendar, ExternalLink, Loader2, ShieldCheck, Sparkles, Tag, Flag, X,
} from "lucide-react";
import { API } from "@/lib/api";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";
import ListenButton from "@/components/socialpulse/ListenButton";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

function articleToPlainText(p) {
  if (!p) return "";
  const parts = [p.title, p.summary, p.intro];
  (p.sections || []).forEach((s) => {
    parts.push(s.heading);
    parts.push(s.body);
    (s.subsections || []).forEach((sub) => {
      parts.push(sub.heading);
      parts.push(sub.body);
    });
  });
  (p.faqs || []).forEach((f) => { parts.push(f.q); parts.push(f.a); });
  return parts.filter(Boolean).join(". ");
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

export default function PulseArticle() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const portalSlug = search.get("portal") || "socialpulse";

  const [article, setArticle] = useState(null);
  const [payload, setPayload] = useState(null);   // currently displayed (master OR translated)
  const [lang, setLang] = useState("en");
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportKind, setReportKind] = useState("outdated");
  const [reportMsg, setReportMsg] = useState("");
  const [reportEmail, setReportEmail] = useState("");
  const [reportSent, setReportSent] = useState(false);

  const submitReport = async () => {
    if (!reportMsg.trim()) return;
    setReportBusy(true);
    try {
      await axios.post(`${API}/content-engine/articles/${article.id}/report`, {
        article_id: article.id,
        kind: reportKind,
        lang,
        message: reportMsg.trim(),
        reporter_email: reportEmail.trim() || null,
      }, { timeout: 8000 });
      setReportSent(true);
    } catch (e) {
      alert(e.response?.data?.detail || e.message || "Failed to send report");
    } finally {
      setReportBusy(false);
    }
  };

  // Fetch master article + language catalogue.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [artRes, langRes] = await Promise.all([
          axios.get(`${API}/content-engine/articles/${encodeURIComponent(slug)}`, { params: { portal: portalSlug }, timeout: 8000 }),
          axios.get(`${API}/content-engine/languages`, { timeout: 4000 }),
        ]);
        if (cancelled) return;
        setArticle(artRes.data);
        setPayload(artRes.data.master);
        setLang(artRes.data.master_language || "en");
        setLanguages(langRes.data.items || []);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, portalSlug]);

  // On lang change, fetch translated payload (cached server-side).
  useEffect(() => {
    if (!article) return;
    if (lang === article.master_language) { setPayload(article.master); return; }
    let cancelled = false;
    setTxLoading(true);
    axios.get(`${API}/content-engine/articles/${encodeURIComponent(slug)}/i18n`, { params: { lang, portal: portalSlug }, timeout: 30000 })
      .then((r) => { if (!cancelled) setPayload(r.data.payload); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setTxLoading(false); });
    return () => { cancelled = true; };
  }, [lang, article, slug, portalSlug]);

  // SEO — reflect the current payload into <head>.
  useEffect(() => {
    if (!payload || !article) return;
    document.title = payload.seo_title || payload.title || "SocialPulse article";
    setMeta("description", payload.meta_description || payload.summary || "");
    setMeta("robots", "index, follow");
    setLink("canonical", article.canonical_url || window.location.href);
    setMeta("og:title", payload.title || "", "property");
    setMeta("og:description", payload.summary || "", "property");
    setMeta("og:type", "article", "property");
    if (article.image?.url_regular) setMeta("og:image", article.image.url_regular, "property");
  }, [payload, article]);

  const plainText = useMemo(() => articleToPlainText(payload), [payload]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" data-testid="sp-article-loading" />
      </div>
    );
  }
  if (err || !article || !payload) {
    return (
      <div className="min-h-screen bg-white">
        <SocialPulseHeader />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <p className="text-slate-500">Article unavailable.</p>
          <p className="text-xs text-rose-500 mt-2">{err}</p>
          <Link to="/" className="text-violet-700 font-semibold hover:underline mt-4 inline-block">← Back to home</Link>
        </div>
        <SocialPulseFooter />
      </div>
    );
  }

  const image = article.image;
  const tags = payload.tags || {};

  return (
    <div className="min-h-screen bg-white" data-testid="sp-article-page">
      <SocialPulseHeader />

      {/* Hero image */}
      {image?.url_regular && (
        <div className="w-full max-h-[420px] overflow-hidden bg-slate-100">
          <img
            src={image.url_regular}
            alt={image.alt_text || payload.title}
            className="w-full h-full object-cover"
            style={{ maxHeight: 420, width: "100%" }}
            data-testid="sp-article-hero-image"
          />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Eyebrow chips */}
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[.15em] text-slate-500 mb-4">
          <span className="inline-flex items-center gap-1 text-violet-700">
            <Sparkles className="w-3 h-3" /> {article.portal_name}
          </span>
          {tags.content_type && (
            <>
              <span className="text-slate-300">·</span>
              <span>{tags.content_type}</span>
            </>
          )}
          <span className="text-slate-300">·</span>
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <ShieldCheck className="w-3 h-3" /> {article.verification_status?.replaceAll("-", " ")}
          </span>
        </div>

        {/* Title + summary */}
        <h1
          data-testid="sp-article-title"
          className="font-serif font-bold tracking-tight text-slate-900 leading-[1.08] text-3xl sm:text-4xl lg:text-5xl"
        >
          {payload.title}
        </h1>
        {payload.summary && (
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">{payload.summary}</p>
        )}

        {/* Meta row: Listen + language + last-updated */}
        <div className="mt-6 flex flex-wrap items-center gap-3 pb-6 border-b border-slate-200">
          <ListenButton text={plainText} lang={lang} articleId={article.id} testid="sp-article-listen" />

          <label className="inline-flex items-center gap-2 text-xs text-slate-600 ml-auto">
            <span className="font-semibold uppercase tracking-widest text-slate-500 text-[10px]">Language</span>
            <div className="relative">
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                data-testid="sp-article-lang-select"
                className="appearance-none rounded-full bg-white ring-1 ring-slate-300 hover:ring-slate-400 focus:ring-violet-500 focus:outline-none pl-3 pr-8 py-1.5 text-xs font-semibold text-slate-700"
              >
                {languages.map((l) => (
                  <option key={l.code} value={l.code}>{l.native} · {l.name}</option>
                ))}
              </select>
              {txLoading && <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-violet-500" />}
            </div>
          </label>

          <span className="text-xs text-slate-500 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Updated {formatDate(article.last_updated)}
          </span>
        </div>

        {/* Intro */}
        {payload.intro && (
          <div className="mt-8 prose prose-slate max-w-none text-slate-700 leading-relaxed" data-testid="sp-article-intro">
            {payload.intro.split(/\n\n+/).map((p, i) => (
              <p key={i} className="mb-4">{p}</p>
            ))}
          </div>
        )}

        {/* Key facts */}
        {(payload.key_facts || []).length > 0 && (
          <aside className="mt-8 rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-5" data-testid="sp-article-key-facts">
            <h2 className="text-[11px] font-semibold uppercase tracking-[.18em] text-slate-500 mb-3">Key facts</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {payload.key_facts.map((f, i) => (
                <div key={i} className="flex flex-col">
                  <dt className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{f.label}</dt>
                  <dd className="text-sm text-slate-800 mt-0.5">{f.value}</dd>
                </div>
              ))}
            </dl>
          </aside>
        )}

        {/* Sections */}
        {(payload.sections || []).map((sec, i) => (
          <section key={i} className="mt-10" data-testid={`sp-article-section-${i}`}>
            <h2 className="font-serif font-bold text-slate-900 text-2xl leading-tight mb-3">
              {sec.heading}
            </h2>
            {sec.body && (
              <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed">
                {sec.body.split(/\n\n+/).map((p, j) => <p key={j} className="mb-4">{p}</p>)}
              </div>
            )}
            {(sec.subsections || []).map((sub, k) => (
              <div key={k} className="mt-5 pl-4 border-l-2 border-slate-100">
                <h3 className="font-serif font-semibold text-slate-900 text-lg mb-2">{sub.heading}</h3>
                {sub.body && (
                  <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed">
                    {sub.body.split(/\n\n+/).map((p, j) => <p key={j} className="mb-3">{p}</p>)}
                  </div>
                )}
              </div>
            ))}
          </section>
        ))}

        {/* FAQs */}
        {(payload.faqs || []).length > 0 && (
          <section className="mt-12" data-testid="sp-article-faqs">
            <h2 className="font-serif font-bold text-slate-900 text-2xl mb-4">Frequently asked</h2>
            <ul className="space-y-4">
              {payload.faqs.map((f, i) => (
                <li key={i} className="rounded-xl bg-white ring-1 ring-slate-200 p-4">
                  <div className="font-semibold text-slate-900 text-sm">{f.q}</div>
                  <div className="text-sm text-slate-600 mt-1 leading-relaxed">{f.a}</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Sources */}
        {(payload.sources || []).length > 0 && (
          <section className="mt-12" data-testid="sp-article-sources">
            <h2 className="text-[11px] font-semibold uppercase tracking-[.18em] text-slate-500 mb-3">Sources</h2>
            <ul className="space-y-2">
              {payload.sources.map((s, i) => (
                <li key={i} className="text-sm">
                  {s.url ? (
                    <a href={s.url} target="_blank" rel="noopener" className="text-violet-700 hover:underline inline-flex items-center gap-1">
                      {s.title} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-slate-700">{s.title}</span>
                  )}
                  {s.publisher && <span className="text-slate-500"> · {s.publisher}</span>}
                  {s.retrieval_date && <span className="text-slate-400 text-[11px]"> · retrieved {s.retrieval_date}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Tags footer */}
        {(tags.geography?.length || tags.topic?.length) && (
          <div className="mt-10 flex flex-wrap gap-1.5 items-center" data-testid="sp-article-tags">
            <Tag className="w-3 h-3 text-slate-400" />
            {[...(tags.topic || []), ...(tags.geography || [])].map((t) => (
              <span key={t} className="rounded-full bg-slate-50 ring-1 ring-slate-200 text-slate-600 px-2 py-0.5 text-[11px] font-semibold">
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Image attribution */}
        {image?.attribution_html && (
          <p
            className="mt-8 text-[11px] text-slate-500"
            dangerouslySetInnerHTML={{ __html: image.attribution_html }}
          />
        )}

        {/* Freshness stamp */}
        <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3 text-violet-500" /> Trust: <b className="text-slate-700 ml-0.5">{article.trust_status}</b></span>
          <span>·</span>
          <span>Last verified {formatDate(article.last_verified)}</span>
          <span>·</span>
          <span>Next review {formatDate(article.next_review_date)}</span>
          <button
            type="button"
            onClick={() => { setReportOpen(true); setReportSent(false); }}
            data-testid="sp-article-report-btn"
            className="ml-auto inline-flex items-center gap-1 rounded-full ring-1 ring-slate-300 hover:ring-rose-400 text-slate-600 hover:text-rose-700 px-2.5 py-1 text-[11px] font-semibold transition-colors"
          >
            <Flag className="w-3 h-3" /> Report an issue
          </button>
        </div>
      </article>

      {/* Report modal */}
      {reportOpen && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4"
          data-testid="sp-article-report-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setReportOpen(false); }}
        >
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 relative">
            <button
              type="button"
              onClick={() => setReportOpen(false)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            {reportSent ? (
              <div className="text-center py-8" data-testid="sp-article-report-sent">
                <div className="w-12 h-12 rounded-full bg-emerald-100 grid place-items-center mx-auto">
                  <BadgeCheck className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="mt-4 font-serif font-bold text-slate-900 text-lg">Thanks — we've got it.</p>
                <p className="mt-1 text-sm text-slate-500">Our editors will review this within 48 hours.</p>
                <button
                  type="button"
                  onClick={() => setReportOpen(false)}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2 text-sm"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-serif font-bold text-slate-900 text-lg">Report an issue</h3>
                <p className="text-xs text-slate-500 mt-1">Anonymous — email is optional but helps us reply.</p>
                <label className="block mt-4">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">What's wrong?</span>
                  <select
                    value={reportKind}
                    onChange={(e) => setReportKind(e.target.value)}
                    data-testid="sp-article-report-kind"
                    className="mt-1 w-full rounded-xl bg-white ring-1 ring-slate-300 focus:ring-violet-500 focus:outline-none px-3 py-2 text-sm text-slate-800"
                  >
                    <option value="outdated">Outdated info</option>
                    <option value="wrong-fact">Wrong fact</option>
                    <option value="wrong-image">Wrong image</option>
                    <option value="wrong-translation">Translation issue</option>
                    <option value="accessibility">Accessibility problem</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label className="block mt-3">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Details</span>
                  <textarea
                    value={reportMsg}
                    onChange={(e) => setReportMsg(e.target.value)}
                    rows={4}
                    placeholder="What did you see? What's the correction?"
                    data-testid="sp-article-report-message"
                    className="mt-1 w-full rounded-xl bg-white ring-1 ring-slate-300 focus:ring-violet-500 focus:outline-none px-3 py-2 text-sm text-slate-800"
                  />
                </label>
                <label className="block mt-3">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Your email (optional)</span>
                  <input
                    type="email"
                    value={reportEmail}
                    onChange={(e) => setReportEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="sp-article-report-email"
                    className="mt-1 w-full rounded-xl bg-white ring-1 ring-slate-300 focus:ring-violet-500 focus:outline-none px-3 py-2 text-sm text-slate-800"
                  />
                </label>
                <button
                  type="button"
                  onClick={submitReport}
                  disabled={reportBusy || !reportMsg.trim()}
                  data-testid="sp-article-report-submit"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2 text-sm disabled:opacity-60"
                >
                  {reportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
                  Send report
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <SocialPulseFooter />
    </div>
  );
}
